// src/pages/Journal.js
import React, { useState, useEffect, useMemo } from 'react';
import { ContentLayout, PageContainer } from '../components/layout/Layout';
import { useJournal } from '../components/doctorsJournal/JournalContext';
import { JournalPasswordSetup } from '../components/doctorsJournal/JournalPasswordSetup';
import { JournalUnlock } from '../components/doctorsJournal/JournalUnlock';
import { JournalEntryCard } from '../components/doctorsJournal/JournalEntryCard';
import { JournalEntryModal } from '../components/doctorsJournal/JournalEntryModal';
import { encryptText, decryptText } from '../components/doctorsJournal/encryptionService';
import { journalService } from '../components/doctorsJournal/journalService';
import { useCustomersPG } from '../hooks/useCustomersPG';
import { Plus, Lock, Unlock as UnlockIcon, Loader2, FileText, Download, ArrowUpDown } from 'lucide-react';
import { generateJournalPDF } from '../utils/journalPdfGenerator';

export function Journal({ onNavigate }) {
  const { isUnlocked, encryptionKey, hasPassword, setupPassword, unlock, lock, loading: journalLoading, resetTimeout } = useJournal();
  const { customers, searchCustomers, loadAllCustomers } = useCustomersPG();

  const [pageSize, setPageSize] = useState(parseInt(localStorage.getItem("journal_page_size") || 10));
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  const [showUnlock, setShowUnlock] = useState(false);
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalEntries, setTotalEntries] = useState(0);
  const [customerFilter, setCustomerFilter] = useState('');
  const [showCustomerFilterDropdown, setShowCustomerFilterDropdown] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState(false);

  // Check if password setup needed
  useEffect(() => {
    if (!hasPassword) {
      setShowPasswordSetup(true);
    }
  }, [hasPassword]);

  // Load entries and customers when unlocked
  useEffect(() => {
    if (isUnlocked) {
      loadEntries();
      loadAllCustomers();
    }
  }, [isUnlocked]);

  useEffect(() => {
    if (isUnlocked) {
      loadEntries(true); // reset=true when filter changes
    }
  }, [customerFilter, pageSize, sortOrder]);

  const loadEntries = async (reset = true) => {
    if (reset) {
      setLoading(true);
      setCurrentPage(1);
    } else {
      setLoadingMore(true);
    }

    try {
      const page = reset ? 1 : currentPage + 1;
      const response = await journalService.getEntriesPaginated(
        page,
        pageSize,
        customerFilter || null,
        sortOrder
      );

      if (response.success) {
        const customers = await loadAllCustomers();
        const actualData = response.data.data
        const decryptedEntries = actualData.map(entry => {
          const decryptedString = decryptText(entry.encrypted_content, encryptionKey);
          let sessionData = {};
          try {
            sessionData = JSON.parse(decryptedString);
          } catch (e) {
            console.error('Failed to parse session data:', e);
          }
          return {
            ...entry,
            sessionData,
            customer: customers.find(c => c.id === entry.customer_id),
          };
        });

        setEntries(prev => reset ? decryptedEntries : [...prev, ...decryptedEntries]);
        setHasMore(response.data.hasMore);
        setTotalEntries(response.data.total);
        if (!reset) setCurrentPage(page);
      }
    } catch (error) {
      console.error('Failed to load entries:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleSetupPassword = (password) => {
    setupPassword(password);
    setShowPasswordSetup(false);
  };

  const handleUnlock = async (password) => {
    const success = await unlock(password);
    if (success) {
      setShowUnlock(false);
    }
    return success;
  };

  const handleViewEntry = (entry) => {
    setSelectedEntry(entry);
    setViewMode(true);  // Set view mode
    setShowEntryModal(true);
  };

  const handleEditEntry = (entry) => {
    setSelectedEntry(entry);
    setViewMode(false);  // Set edit mode
    setShowEntryModal(true);
  };

  const handleCloseModal = () => {
    setShowEntryModal(false);
    setSelectedEntry(null);
    setViewMode(false);
    loadEntries();
  };

  const handleSaveEntry = async (entryData) => {
    try {
      // Serialize and encrypt all session data
      const sessionDataString = JSON.stringify(entryData.sessionData);
      const encryptedContent = encryptText(sessionDataString, encryptionKey);

      const payload = {
        customer_id: entryData.customer_id,
        session_number: entryData.session_number,
        entry_date: entryData.entry_date,
        encrypted_content: encryptedContent,
      };

      let response;
      if (entryData.id) {
        response = await journalService.updateEntry(entryData.id, payload);
      } else {
        response = await journalService.createEntry(payload);
      }

      if (response.success) {
        await loadEntries();
        setShowEntryModal(false);
        setSelectedEntry(null);
      }
    } catch (error) {
      console.error('Failed to save entry:', error);
    }
  };

  const handleAutoSave = async (entryData) => {
    try {
      const sessionDataString = JSON.stringify(entryData.sessionData);
      const encryptedContent = encryptText(sessionDataString, encryptionKey);
      const payload = {
        customer_id: entryData.customer_id,
        session_number: entryData.session_number,
        entry_date: entryData.entry_date,
        encrypted_content: encryptedContent,
      };

      let response;
      if (entryData.id) {
        response = await journalService.updateEntry(entryData.id, payload);
      } else {
        response = await journalService.createEntry(payload);
      }

      if (response.success) {
        return response.data?.data?.id || response.data?.id || entryData.id;
      }
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
    return null;
  };

  const handleDeleteEntry = async (entry) => {
    if (!window.confirm('האם למחוק רשומה זו? פעולה זו אינה ניתנת לביטול.')) {
      return;
    }

    try {
      const response = await journalService.deleteEntry(entry.id);
      if (response.success) {
        await loadEntries();
      }
    } catch (error) {
      console.error('Failed to delete entry:', error);
    }
  };

  const handleCustomerClick = (customer) => {
    if (customer?.id) {
      onNavigate('customer-detail', customer.id);
    }
  };

  const [exportingPDF, setExportingPDF] = useState(false);

  const handleExportPDF = async () => {
    setExportingPDF(true);
    try {
      const allEntries = [];
      let page = 1;
      const batchSize = 100;
      let hasMorePages = true;
      const allCustomers = await loadAllCustomers();

      while (hasMorePages) {
        const response = await journalService.getEntriesPaginated(page, batchSize, customerFilter || null, sortOrder);
        if (!response.success) break;

        const decrypted = response.data.data.map(entry => {
          let sessionData = {};
          try {
            sessionData = JSON.parse(decryptText(entry.encrypted_content, encryptionKey));
          } catch (e) { /* skip */ }
          return {
            ...entry,
            sessionData,
            customer: allCustomers.find(c => c.id === entry.customer_id),
          };
        });

        allEntries.push(...decrypted);
        hasMorePages = response.data.hasMore;
        page++;
      }

      const customerName = customerFilter
        ? customers.find(c => c.id === customerFilter)?.full_name || null
        : null;

      const blob = generateJournalPDF(allEntries, customerName, 'יומן טיפולים');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `journal_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export journal PDF:', error);
    } finally {
      setExportingPDF(false);
    }
  };

  const headerActions = isUnlocked ? [
    {
      label: 'רשומה חדשה',
      icon: <Plus className="w-4 h-4" />,
      variant: 'primary',
      onClick: () => {
        setSelectedEntry(null);
        setShowEntryModal(true);
      },
    },
    {
      label: exportingPDF ? 'מייצא...' : 'ייצוא PDF',
      icon: exportingPDF ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />,
      variant: 'secondary',
      onClick: handleExportPDF,
      disabled: exportingPDF || entries.length === 0,
    },
    {
      label: 'נעל יומן',
      icon: <Lock className="w-4 h-4" />,
      variant: 'secondary',
      onClick: lock,
    },
  ] : [];

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    localStorage.setItem("journal_page_size", newSize.toString());
  };

  if (loading || journalLoading) {
    return (
      <ContentLayout
        currentPage="journal"
        onNavigate={onNavigate}
        title="יומן רופא"
        description="רשומות מוצפנות ומאובטחות"
        primaryAction={headerActions[0]}
        secondaryActions={headerActions.slice(1)}
      >
        <div className="flex flex-col justify-center items-center py-12">
            <Loader2 className="w-8 h-8 mb-6 text-pink-500 animate-spin" />
            המערכת טוענת נתונים...
        </div>
      </ContentLayout>
    )
  }

  return (
    <ContentLayout
      currentPage="journal"
      onNavigate={onNavigate}
      title="יומן רופא"
      description="רשומות מוצפנות ומאובטחות"
      primaryAction={headerActions[0]}
      secondaryActions={headerActions.slice(1)}
    >
      <PageContainer>
        {/* Password Setup Modal */}
        {!journalLoading && !hasPassword && (<JournalPasswordSetup
          isOpen={showPasswordSetup}
          onSetup={handleSetupPassword}
        />)}

        {/* Unlock Modal */}
        <JournalUnlock
          isOpen={showUnlock}
          onUnlock={handleUnlock}
          onCancel={() => setShowUnlock(false)}
        />

        {/* Entry Modal */}
        {isUnlocked && (
          <JournalEntryModal
            isOpen={showEntryModal}
            onClose={handleCloseModal}
            onSave={handleSaveEntry}
            onAutoSave={handleAutoSave}
            onActivity={resetTimeout}
            entry={selectedEntry}
            customers={customers}
            allEntries={entries}
            viewMode={viewMode}
          />
        )}

        {/* Locked State */}
        {!isUnlocked && hasPassword && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="max-w-md text-center">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full flex items-center justify-center">
                <Lock className="w-12 h-12 text-pink-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">היומן נעול</h2>
              <p className="text-gray-600 mb-8">
                הזן סיסמת יומן כדי לצפות ולערוך רשומות מוצפנות
              </p>
              <button
                onClick={() => setShowUnlock(true)}
                className="px-8 py-3 bg-gradient-to-l from-pink-500 to-purple-500 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl inline-flex items-center gap-2"
              >
                <UnlockIcon className="w-5 h-5" />
                פתח יומן
              </button>
            </div>
          </div>
        )}

        {/* Unlocked State */}
        {isUnlocked && (
          <>
            {/* Loading */}
            {loading && (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
                המערכת טוענת נתונים...
              </div>
            )}

            {/* Empty State */}
            {!loading && entries.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full flex items-center justify-center">
                  <FileText className="w-12 h-12 text-pink-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">אין רשומות עדיין</h3>
                <p className="text-gray-600 mb-6">התחל על ידי יצירת הרשומה הראשונה</p>
                <button
                  onClick={() => setShowEntryModal(true)}
                  className="px-6 py-3 bg-gradient-to-l from-pink-500 to-purple-500 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl inline-flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  רשומה חדשה
                </button>
              </div>
            )}

            {isUnlocked && (
              <div className="mb-1 md:mb-6 flex items-center gap-3">
                <div className="relative max-w-md flex-1">
                  <input
                    type="text"
                    placeholder="סנן לפי לקוח..."
                    value={customerSearchTerm}
                    onChange={(e) => {
                      setCustomerSearchTerm(e.target.value);
                      setShowCustomerFilterDropdown(true);
                    }}
                    onFocus={() => setShowCustomerFilterDropdown(true)}
                    onBlur={() => setTimeout(() => setShowCustomerFilterDropdown(false), 100)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500"
                  />
                  {customerFilter && (
                    <button
                      onClick={() => {
                        setCustomerFilter('');
                        setCustomerSearchTerm('');
                      }}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  )}

                  {showCustomerFilterDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-xl shadow-lg max-h-48 overflow-y-auto">
                      {customers
                        .filter(c => c.full_name?.toLowerCase().includes(customerSearchTerm.toLowerCase()))
                        .slice(0, 10)
                        .map(c => (
                          <div
                            key={c.id}
                            onClick={() => {
                              setCustomerFilter(c.id);
                              setCustomerSearchTerm(c.full_name);
                              setShowCustomerFilterDropdown(false);
                            }}
                            className="px-4 py-2 hover:bg-pink-50 cursor-pointer"
                          >
                            {c.full_name}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setSortOrder(o => o === 'desc' ? 'asc' : 'desc')}
                  className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-xl text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap"
                  title={sortOrder === 'desc' ? 'מיון: חדש לישן' : 'מיון: ישן לחדש'}
                >
                  <ArrowUpDown className="w-4 h-4" />
                  {sortOrder === 'desc' ? 'חדש לישן' : 'ישן לחדש'}
                </button>
              </div>
            )}

            {/* Entries Grid */}
            {!loading && entries.length > 0 && (
              <div className="grid grid-cols-1 gap-3">
                {entries.map(entry => (
                  <JournalEntryCard
                    key={entry.id}
                    entry={entry}
                    isLocked={false}
                    onEdit={() => handleEditEntry(entry)}
                    onDelete={handleDeleteEntry}
                    onClick={() => handleViewEntry(entry)}
                    onCustomerClick={handleCustomerClick}
                    searchCustomers={searchCustomers}
                    allEntries={entries}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Load More */}
        {isUnlocked && !loading && entries.length > 0 && (
          <div className="mt-8 text-center pb-8">
            <div className="text-sm text-gray-500 mb-4">
              מציג {entries.length} מתוך {totalEntries} רשומות
            </div>
            {hasMore && (
              <button
                onClick={() => loadEntries(false)}
                disabled={loadingMore}
                className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    טוען...
                  </>
                ) : (
                  'טען עוד'
                )}
              </button>
            )}
          </div>
        )}

        {/* Page Size Selector */}
        {isUnlocked && !loading && entries.length > 0 && (
          <div className="flex items-center justify-center gap-3 mt-6 pt-6 border-t border-gray-200">
            <span className="text-sm text-gray-600">רשומות בעמוד:</span>
            <div className="flex gap-2">
              {[5, 10, 15, 25, 50].map(size => (
                <button
                  key={size}
                  onClick={() => handlePageSizeChange(size)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${pageSize === size
                    ? 'bg-gradient-to-l from-pink-500 to-purple-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        )}
      </PageContainer>
    </ContentLayout>
  );
}